// The wheel between GG and BHARAT: Konark's sun wheel, from the chariot of the Sun Temple (its wheels are also
// sundials): eight broad spokes with medallions, eight slim ones, a beaded rim and a lotus at the hub. Deliberately
// not the Ashoka Chakra: India's Emblems and Names (Prevention of Improper Use) Act, 1950 (Schedule, item 16) bars the
// Ashoka Chakra, and anything imitating it, from business use, trade marks and designs without the Centre's permission.
import { useId } from 'react';

const TAU = Math.PI * 2;
const pt = (r: number, a: number) => `${(Math.cos(a) * r).toFixed(2)} ${(Math.sin(a) * r).toFixed(2)}`;

/** a spoke: a bar from the hub to the rim, a little wider at the hub */
function spoke(a: number, w0: number, w1: number, r0 = 29, r1 = 81) {
  const n = a + Math.PI / 2;
  const off = (r: number, w: number, s: number) => `${(Math.cos(a) * r + Math.cos(n) * w * s).toFixed(2)} ${(Math.sin(a) * r + Math.sin(n) * w * s).toFixed(2)}`;
  return `M${off(r0, w0, 1)}L${off(r1, w1, 1)}L${off(r1, w1, -1)}L${off(r0, w0, -1)}Z`;
}
/** a lotus petal round the hub */
function petal(a: number) {
  const w = TAU / 32;
  return `M${pt(19, a - w)}Q${pt(27, a - w * 1.1)} ${pt(31, a)}Q${pt(27, a + w * 1.1)} ${pt(19, a + w)}Z`;
}

const MAJOR = Array.from({ length: 8 }, (_, i) => (i * TAU) / 8 - Math.PI / 2);
const MINOR = MAJOR.map((a) => a + TAU / 16);
const PATH_MAJOR = MAJOR.map((a) => spoke(a, 7.2, 5.4)).join('');
const PATH_MINOR = MINOR.map((a) => spoke(a, 2.4, 2.1)).join('');
const PATH_PETALS = Array.from({ length: 16 }, (_, i) => petal((i * TAU) / 16)).join('');
const BEADS = Array.from({ length: 40 }, (_, i) => (i * TAU) / 40);

export function Wheel({ className, label }: { className?: string; label?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg className={className} viewBox="-100 -100 200 200" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <defs>
        <linearGradient id={`wk-g${id}`} x1="0" y1="-1" x2="0" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="var(--wk-hi, #ffe1a1)" />
          <stop offset="0.55" stopColor="var(--wk-mid, #ffab4a)" />
          <stop offset="1" stopColor="var(--wk-lo, #e2632a)" />
        </linearGradient>
      </defs>
      <g fill={`url(#wk-g${id})`}>
        {/* the rim: a band, its beads, a thin inner ring */}
        <path d="M0 -98A98 98 0 1 1 0 98A98 98 0 1 1 0 -98ZM0 -84A84 84 0 1 0 0 84A84 84 0 1 0 0 -84Z" fillRule="evenodd" />
        <path d={PATH_MAJOR} />
        <path d={PATH_MINOR} />
        <path d="M0 -31A31 31 0 1 1 0 31A31 31 0 1 1 0 -31ZM0 -26A26 26 0 1 0 0 26A26 26 0 1 0 0 -26Z" fillRule="evenodd" />
        <path d={PATH_PETALS} />
        <circle r="15" />
        {MAJOR.map((a) => (
          <circle key={a} cx={Math.cos(a) * 56} cy={Math.sin(a) * 56} r="10.5" />
        ))}
        {MINOR.map((a) => (
          <circle key={a} cx={Math.cos(a) * 56} cy={Math.sin(a) * 56} r="4" />
        ))}
      </g>
      {/* the carving: darker cuts in the paper */}
      <g fill="var(--wk-cut, rgba(60,18,30,0.55))">
        {BEADS.map((a) => (
          <circle key={a} cx={Math.cos(a) * 91} cy={Math.sin(a) * 91} r="2.6" />
        ))}
        {MAJOR.map((a) => (
          <circle key={a} cx={Math.cos(a) * 56} cy={Math.sin(a) * 56} r="5.2" />
        ))}
        <circle r="7.5" />
      </g>
      <circle r="3" fill={`url(#wk-g${id})`} />
    </svg>
  );
}
